import type {EventRuleChange} from '../../types'

import Future from 'fluture'
import {dissoc, head, map, pick, pipe, prop, toPairs, values, objOf, merge,
  find, propEq} from 'ramda'
import {toRuleChanges, toSubRuleChanges, combineRuleChanges} from '@/rules-helper'
import {startDate as defaultStartDate, endDate as defaultEndDate, recentEventsOptions,
  scheduledExportSortOptions, previousExportSortOptions} from '@/defaults'
import {portfoliosSelectors} from '@/store'
import {
  getPortfolioCompaniesQuery,
  getPortfolioCompaniesByRiskTypeQuery,
  getScheduledExportsQuery,
  getPreviousExportsQuery,
  fetchHomeTabData,
  fetchPortfoliosTabData,
  fetchAnalysisTabData,
  findDefaultPortfolio,
} from '@/store/common'
import {
  prependIndexerString,
  getCompaniesSafeNumbers,
  convertJSONToFileObject,
  getDefaultPortfolioId,
  getPortfolioName,
} from './util'
import {sequenceFuturesObject} from '@/store/future-helpers'
import {mergePortfolios} from '@/store/util'

const waitSomeTimeForProcessing = (time = 500, data) => Future.after(time, data)

const getActivePortfolio = (activeId) => (portfolios) => {
  let activePortfolio
  if (activeId) {
    activePortfolio = find(propEq('portfolioId', activeId), portfolios)
  }
  if (!activePortfolio) {
    activePortfolio = findDefaultPortfolio(portfolios)
  }
  return activePortfolio
}

const addActivePortfolio = (activeId) => (portfoliosObj) => {
  const activePortfolio = getActivePortfolio(activeId)(portfoliosObj.portfolios)
  if (activePortfolio) {
    const id = activePortfolio.portfolioId
    return Future.of(merge(portfoliosObj)(objOf('activePortfolio')({portfolioId: id})))
  }
  const id = portfoliosObj.portfolios.length > 0 ? portfoliosObj.portfolios[0].portfolioId : 0
  return Future.of(merge(portfoliosObj)(objOf('activePortfolio')({portfolioId: id})))
}

export const saveCompany = (companyId: string, portfolioId: number) => ({api, getState}) => {
  const activePortfolioId = getState().app.activePortfolioId
  const companyData = getState().form.data
  const safeNumber = companyId.includes('_') ? companyId.split('_')[1] : companyId
  const payload =
    api.savePortfolioCompany(portfolioId, safeNumber)(companyData)

  return {
    type: 'UPDATE_COMPANY_DETAILS',
    activePortfolioId,
    companyId,
    companyData,
    payload,
  }
}

const buildFileData = (d, t) => {
  const data = new FormData()
  const file = t === 'file' ? d.file : convertJSONToFileObject(d.manual)
  data.append('importCsv', file, file.name)
  return data
}

export const importCompaniesFile = (type) => ({api, getState}) => {
  const fileData = buildFileData(getState().form.data, type)
  const portfolioId = getState().app.activePortfolioId
  const payload =
    api.importCompaniesFile(portfolioId)(fileData)
      .chain((data) => waitSomeTimeForProcessing(5000, data))

  return {
    type: 'IMPORT_COMPANIES_FILE',
    payload,
    portfolioId,
  }
}

export const syncCompaniesFile = (type) => ({api, getState}) => {
  const fileData = buildFileData(getState().form.data, type)
  const portfolioId = getState().app.activePortfolioId
  const payload =
    api.syncCompaniesFile(portfolioId)(fileData)
      .chain((data) => waitSomeTimeForProcessing(5000, data))

  return {
    type: 'SYNC_COMPANIES_FILE',
    payload,
    portfolioId,
  }
}

export const setupEditCompanyForm = (companyId: string) => ({
  type: 'SETUP_FORM',
  formType: 'edit-company',
  id: companyId,
})

export const setupAddPortfolioForm = () => ({
  type: 'SETUP_FORM',
  formType: 'add-portfolio',
})

export const setupEditPortfolioForm = (portfolioId: number) => ({
  type: 'SETUP_FORM',
  formType: 'edit-portfolio',
  id: portfolioId.toString(),
})

export const setupSharePortfolioForm = () => ({
  type: 'SETUP_FORM',
  formType: 'share-portfolio',
})

export const setupPortfolioListForm = () => ({
  type: 'SETUP_FORM',
  formType: 'portfolio-multi-select',
})

export const setupNewExportForm = () => ({
  type: 'SETUP_FORM',
  formType: 'export',
})

export const setupEditExportForm = (exportId: number) => ({
  type: 'SETUP_FORM',
  formType: 'export',
  id: exportId.toString(),
})

export const setupUploadFileForm = (type) => ({
  type: 'SETUP_FORM',
  formType: `upload-file-${type}`,
})

export const addNewExport = () => ({api, getState}) => {
  const exportData = getState().form.data
  const payload = api.addPortfolioExport(exportData.portfolioId)(exportData)

  return {
    type: 'ADD_EXPORT',
    payload,
    formData: exportData,
    activePortfolioId: getState().app.activePortfolioId,
  }
}

export const saveExport = (exportId: number, name: string) => ({api, getState}) => {
  const exportData = getState().form.data
  const portfolioId = getState().app.activePortfolioId
  const payload = api.savePortfolioExport(portfolioId, name)(exportData)

  return {
    type: 'SAVE_EXPORT',
    payload,
    formData: exportData,
    exportId,
  }
}

export const deleteExport = (exportId: number, name: string) => ({api, getState}) => {
  const portfolioId = getState().app.activePortfolioId
  const payload =
    api.deletePortfolioExport({portfolioId, name})

  return {
    type: 'DELETE_EXPORT',
    portfolioId,
    exportId,
    payload,
  }
}

export const updateSelectedCompanies = (list) => ({api, getState}) => {
  const portfolioId = getState().app.activePortfolioId
  const payload = {
    list: {...list},
  }
  return {
    type: 'UPDATE_SELECTED_COMPANIES',
    payload,
    portfolioId,
  }
}

export const addPortfolio = () => ({api, getState}) => {
  const portfolioData = getState().form.data
  const portfolioId = getState().app.activePortfolioId

  const payload =
    api.addPortfolio(portfolioData)
      .chain(() => api.fetchPortfolios({
        page: 0,
        pageSize: 1000,
      })).map(mergePortfolios)

  return {
    type: 'ADD_PORTFOLIO',
    payload,
    portfolioData,
    activePortfolioId: portfolioId,
  }
}

export const getPortfolioData = () => ({api, getState}) => {
  const portfolioId = getState().app.activePortfolioId
  const payload = api.fetchActivePortfolio(portfolioId)

  return {
    type: 'GET_PORTFOLIO_DATA',
    portfolioId,
    payload,
  }
}

export const updateActivePortfolio = () => ({api, getState}) => {
  const portfolioId = getState().app.activePortfolioId
  const portfolioData = getState().form.data

  const payload =
    api.updatePortfolio(portfolioId)(portfolioData)
      .chain(() => api.fetchPortfolios({
        page: 0,
        pageSize: 1000,
      })).map(mergePortfolios)

  return {
    type: 'UPDATE_PORTFOLIO',
    portfolioId,
    portfolioData,
    payload,
  }
}

export const deleteActivePortfolio = () => ({api, getState}) => {
  const portfolioId = getState().app.activePortfolioId
  const pickNewActiveId = pipe(
    dissoc(prependIndexerString(portfolioId)),
    values,
    head,
    prop('portfolioId'),
  )
  const newActiveId = pickNewActiveId(getState().portfolios)

  const payload = api.deletePortfolio(portfolioId)
    .chain((data) => waitSomeTimeForProcessing(5000, data))

  return {
    type: 'DELETE_PORTFOLIO',
    newActiveId,
    portfolioId,
    payload,
  }
}

export const moveCompanies = () => ({api, getState}) => {
  const state = getState()
  const activeId = state.app.activePortfolioId
  const portfolioIds = state.form.data.selectedPortfolioIds
  const companies = portfoliosSelectors.getSelectedCompanies(state)

  const payload = api.movePortfolioCompanies({
    portfolios: portfolioIds,
    companies: companies.data,
    currentPortfolioId: activeId,
    isAll: companies.isAll,
  })

  return {
    type: 'MOVE_PORTFOLIO_COMPANIES',
    payload,
    portfolioId: activeId,
  }
}

export const copyCompanies = () => ({api, getState}) => {
  const state = getState()
  const activeId = state.app.activePortfolioId
  const portfolioIds = state.form.data.selectedPortfolioIds
  const companies = portfoliosSelectors.getSelectedCompanies(state)
  const payload = api.copyPortfolioCompanies({
    portfolios: portfolioIds,
    companies: companies.data,
    isAll: companies.isAll,
    currentPortfolioId: activeId,
  })

  return {
    type: 'COPY_PORTFOLIO_COMPANIES',
    portfolioId: activeId,
    payload,
  }
}

export const deleteCompanies = () => ({api, getState}) => {
  const state = getState()
  const portfolioId = getState().app.activePortfolioId
  const companies = portfoliosSelectors.getSelectedCompanies(state)
  const payload =
    api.deletePortfolioCompanies({
      companies: getCompaniesSafeNumbers(companies.data),
      isAll: companies.isAll,
      currentPortfolioId: portfolioId,
    })
  return {
    type: 'DELETE_PORTFOLIO_COMPANIES',
    portfolioId,
    payload,
  }
}

export const fetchPortfolioCompanies = () => ({api, getState}) => {
  const portfolioId = getState().app.activePortfolioId
  const selectedLanguage = getState().app.user.preferredLanguageCode
  const payload = api.fetchPortfolioCompanies(
    portfolioId,
    getPortfolioCompaniesQuery(getState()),
    'portfolios',
    selectedLanguage,
  )
  return {
    type: 'FETCH_PORTFOLIO_COMPANIES',
    portfolioId,
    payload,
  }
}

export const fetchPortfolioCompaniesByRiskType = () => ({api, getState}) => {
  const portfolioId = getState().app.activePortfolioId
  const selectedLanguage = getState().app.user.preferredLanguageCode
  const payload = api.fetchPortfolioCompanies(
    portfolioId,
    getPortfolioCompaniesByRiskTypeQuery(getState()),
    'analysis',
    selectedLanguage,
  )

  return {
    type: 'FETCH_PORTFOLIO_COMPANIES_BY_RISK_TYPE',
    portfolioId,
    payload,
  }
}

const _fetchPortfolioEvents = (type) =>
  ({startDate = defaultStartDate, endDate = defaultEndDate} = {}) =>
    ({api, getState}) => {
      let portfolioId = getState().app.activePortfolioId
      let portfolios = portfoliosSelectors.getPortfolios(getState())
      let props = getState().ui.modal.props ? getState().ui.modal.props : {}
      let language = getState().app.user.preferredLanguageCode
      props['portfolioName'] = getPortfolioName({activePortfolioId: portfolioId, portfolios})
      let params = {
        startDate,
        endDate,
        language,
      }
      if (type === 'main') {
        params.page = getState().ui.eventsPaging.pageNumber
        params.sortBy = recentEventsOptions.sortBy
        params.sortDir = recentEventsOptions.sortDir
      }
      if (type === 'export') {
        params.page = 0
        params.sortBy = getState().ui.modalEventsOptions.sortBy
        params.sortDir = getState().ui.modalEventsOptions.sortDir
        params.pageSize = getState().ui.modal.props.eventsCount
      } else if (type === 'modal') {
        params.page = getState().ui.modalEventsPaging.pageNumber
        params.sortBy = getState().ui.modalEventsOptions.sortBy
        params.sortDir = getState().ui.modalEventsOptions.sortDir
      }
      const payload = api.fetchPortfolioEvents(
        portfolioId,
        params
      )
      return {
        type: type === 'main' ? 'FETCH_PORTFOLIO_EVENTS'
          : (type === 'export')
            ? 'FETCH_PORTFOLIO_EXPORT_EVENTS'
            : 'FETCH_PORTFOLIO_MODAL_EVENTS',
        portfolioId,
        payload,
        props,
      }
    }

export const fetchPortfolioEvents = _fetchPortfolioEvents('main')

export const fetchPortfolioModalEvents = _fetchPortfolioEvents('modal')

export const fetchPortfolioEventsExport = _fetchPortfolioEvents('export')

export const changeEventRule = (
  country: string,
  groupId: number,
  change: EventRuleChange,
) => ({getState}) => {
  const portfolioId = getState().app.activePortfolioId

  return {
    type: 'EDIT_EVENT_RULE',
    portfolioId,
    country,
    groupId,
    change,
  }
}

export const saveRuleChanges = () => ({api, getState}) => {
  const state = getState()
  const portfolioId = state.app.activePortfolioId
  const rules = portfoliosSelectors.getActivePortfolioRules(state)
  const changedCountries =
    portfoliosSelectors.getActivePortfolioRuleCountriesChanged(state)

  const toRequest = ([countryCode, changes]) =>
    api.savePortfolioRules({portfolioId, countryCode})(changes)

  const buildChangeRequests = pipe(
    pick(changedCountries),
    map(toRuleChanges),
    toPairs,
  )
  const buildSubRuleChangeRequests = pipe(
    pick(changedCountries),
    map(toSubRuleChanges),
    toPairs,
  )
  const totalRuleChanges = combineRuleChanges(buildChangeRequests(rules), buildSubRuleChangeRequests(rules))
  const updateRuleChanges = map(toRequest, totalRuleChanges)
  const payload =
    Future.parallel(Infinity, updateRuleChanges)
  if (payload && payload._futures) {
    payload._futures.forEach((obj, index) => {
      payload._futures[index]._b = obj._b.filter((id) => {
        return id.ruleCode !== undefined
      })
    })
  }
  return {
    type: 'SAVE_PORTFOLIO_RULES',
    portfolioId,
    payload,
  }
}
export const showCompanyReport = (companyId) => ({api, getState}) => {
  const payload = api.getCompanyReportLink({companyId})

  return {
    type: 'OPEN_LINK',
    payload,
  }
}

export const updateGraph = () => ({api, getState}) => {
  const portfolioId = getState().app.activePortfolioId
  const startDate = getState().ui.graphStartDate
  const endDate = getState().ui.graphEndDate
  const payload =
    api.fetchPortfolioEventsGraphData({
      portfolioId,
      startDate,
      endDate,
    })

  return {
    type: 'UPDATE_GRAPH',
    portfolioId,
    payload,
  }
}

export const getPortfolioExportAttachment = ({exportId, historyId, type, name}) => ({api, getState}) => {
  const portfolioId = getState().app.activePortfolioId
  const payload =
    api.fetchPortfolioExportAttachment(portfolioId)({
      exportId,
      historyId,
      type,
      name,
    })
  return {
    type: 'FETCH_EXPORT_ATTACHMENT',
    payload,
  }
}

const _fetchScheduledExports = ({api, getState}) => {
  const portfolioId = getState().app.activePortfolioId
  const payload = api.fetchScheduledExports(portfolioId, getScheduledExportsQuery(getState()))

  return {
    type: 'FETCH_SCHEDULED_EXPORTS',
    payload,
    portfolioId,
  }
}

const _fetchPreviousExports = ({api, getState}) => {
  const portfolioId = getState().app.activePortfolioId
  const payload = api.fetchPreviousExports(portfolioId, getPreviousExportsQuery(getState()))

  return {
    type: 'FETCH_PREVIOUS_EXPORTS',
    payload,
    portfolioId,
  }
}

export const fetchExports = (exportType : string) => ({api, getState}) => {
  return exportType === 'previous' ? _fetchPreviousExports({api, getState}) : _fetchScheduledExports({api, getState})
}

export const resetModalEventsData = () => ({api, getState}) => {
  const portfolioId = getState().app.activePortfolioId
  return {
    type: 'RESET_MODAL_EVENTS_DATA',
    portfolioId,
  }
}

export const fetchHomeTabInitialData = () => ({api, getState}) => {
  const portfolioId = getState().app.activePortfolioId
  const languageCode = getState().app.user.preferredLanguageCode
  const payload = fetchHomeTabData(api)(portfolioId, languageCode)
  return {
    type: 'FETCH_HOME_TAB_DATA',
    payload,
    portfolioId,
  }
}

export const fetchPortfoliosTabInitialData = () => ({api, getState}) => {
  const portfolioId = getState().app.activePortfolioId
  const selectedLanguage = getState().app.user.preferredLanguageCode
  const payload = fetchPortfoliosTabData(api)(portfolioId, selectedLanguage)
  return {
    type: 'FETCH_PORTFOLIOS_TAB_DATA',
    payload,
    portfolioId,
  }
}

export const fetchSettingsTabInitialData = () => ({api, getState}) => {
  let portfolioId = getState().app.activePortfolioId
  const totalPortfolios = values(getState().portfolios)
  if (portfolioId === 0) {
    portfolioId = getDefaultPortfolioId(totalPortfolios)[0].portfolioId
  }
  const payload = api.fetchPortfolioRules(portfolioId)
    .map(objOf('rules'))
  return {
    type: 'FETCH_SETTINGS_TAB_DATA',
    payload,
    portfolioId,
  }
}

export const fetchExportsTabInitialData = () => ({api, getState}) => {
  let portfolioId = getState().app.activePortfolioId
  const totalPortfolios = values(getState().portfolios)
  if (portfolioId === 0) {
    portfolioId = getDefaultPortfolioId(totalPortfolios)[0].portfolioId
  }
  const payload = api.fetchPortfolioExports(portfolioId, scheduledExportSortOptions, previousExportSortOptions)
  return {
    type: 'FETCH_EXPORTS_TAB_DATA',
    payload,
    portfolioId,
  }
}

export const fetchAnalysisTabInitialData = () => ({api, getState}) => {
  const portfolioId = getState().app.activePortfolioId
  const selectedLanguage = getState().app.user.preferredLanguageCode
  const payload = fetchAnalysisTabData(api)(portfolioId, selectedLanguage)
  return {
    type: 'FETCH_ANALYSIS_TAB_DATA',
    payload,
    portfolioId,
  }
}

export const fetchCommonTabsData = () => ({api, getState}) => {
  const portfolioId = getState().app.activePortfolioId
  const payload = api.fetchPortfolioEventsSummary(portfolioId).map(objOf('notificationEventsSummary'))
  return {
    type: 'FETCH_COMMON_TAB_DATA',
    payload,
    portfolioId,
  }
}

export const refreshPortfoliosData = () => ({api, getState}) => {
  const paging = {
    page: 0,
    pageSize: 1000,
  }
  const portfolioId = getState().app.activePortfolioId
  const payload = sequenceFuturesObject({
    portfoliosObj: api.fetchPortfolios(paging).map(mergePortfolios).chain(addActivePortfolio(portfolioId)),
    notifications: api.fetchNotifications({startDate: defaultStartDate, endDate: defaultEndDate, paging}),
  })
  return {
    type: 'GET_PORTFOLIOS_DATA',
    payload,
    portfolioId,
  }
}

export const refreshExportsData = () => {
  return {
    type: 'REFRESH_EXPORTS',
  }
}

export const fetchPortfolioCountries = () => ({api, getState}) => {
  const portfolioId = getState().app.activePortfolioId
  const payload = api.fetchPortfolioCountries(portfolioId).map(objOf('countries'))
  return {
    type: 'GET_PORTFOLIOS_COUNTRIES',
    payload,
    portfolioId,
  }
}
export const updatefilteredNotificationEvents = (filteredData) => ({api, getState}) => {
  const portfolioId = getState().app.activePortfolioId
  return {
    type: 'FILTERED_NOTIFICATIONS_DATA',
    filteredData,
    portfolioId,
  }
}



// WEBPACK FOOTER //
// ./src/store/portfolios/actions.js